// Background HealthKit sync for the iOS native shell.
//
// Syncs once when a signed-in session mounts and again whenever the
// app returns to the foreground. Two resume signals, both wired:
// Capacitor's native appStateChange (fires reliably when iOS resumes
// the process) and the webview's visibilitychange (fires for in-app
// overlays like the Health permission sheet). Throttling (≤1/hour)
// and single-flight de-duplication live in syncHealthKitToday, so the
// overlapping signals can't double-post; every call is a no-op
// off-iOS or while the integration isn't connected.
import { useEffect } from 'react'
import { App as CapacitorApp } from '@capacitor/app'
import { useAuth } from '../context/AuthContext'
import { isHealthKitSupported, syncHealthKitToday } from '../services/healthkit'

export function useHealthKitSync() {
  const { token } = useAuth()

  useEffect(() => {
    if (!token || !isHealthKitSupported()) return undefined

    syncHealthKitToday(token)

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        syncHealthKitToday(token)
      }
    }
    document.addEventListener('visibilitychange', onVisible)

    let appListener
    CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) syncHealthKitToday(token)
    }).then((l) => { appListener = l }).catch(() => { /* web / older shell */ })

    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      appListener?.remove?.()
    }
  }, [token])
}
